/**
 * Census by Doodive — entry point
 * Initialise le routing et la connexion Supabase.
 */

import { supabase } from './supabase.js';
import { t } from './i18n.js';
import { el } from './utils.js';
import { pushRoute, peekBack, popIfBack } from './navigation-stack.js';
import { createBackButton } from '../components/back-button.js';
import { renderHome } from '../pages/home.js';
import { renderSpeciesList } from '../pages/species-list.js';
import { renderSpeciesDetail } from '../pages/species-detail.js';
import { renderSwipe } from '../pages/swipe.js';
import { renderNotFound } from '../pages/not-found.js';
import { renderHelp } from '../pages/help.js';

let currentActivePath = null;
let currentActiveFullUrl = null;

const PAGE_LABEL_KEYS = {
  '/': 'nav.back.home',
  '/species': 'nav.back.catalog',
  '/swipe': 'nav.back.swipe',
};

function getFallbackRoute(path) {
  if (/^\/species\/[^/]+/.test(path)) {
    return { url: '#/species', labelKey: 'nav.back.catalog' };
  }
  return { url: '#/', labelKey: 'nav.back.home' };
}

// ---------- Routes ----------
const routes = [
  { pattern: /^\/?$/, handler: renderHome },
  { pattern: /^\/swipe\/?$/, handler: renderSwipe },
  { pattern: /^\/species\/?$/, handler: renderSpeciesList },
  { pattern: /^\/species\/([^/]+)\/?$/, handler: renderSpeciesDetail, paramNames: ['id'] },
  { pattern: /^\/help\/?$/, handler: renderHelp },
];

function matchRoute(path) {
  for (const route of routes) {
    const match = path.match(route.pattern);
    if (match) {
      const params = {};
      route.paramNames?.forEach((name, i) => {
        params[name] = decodeURIComponent(match[i + 1]);
      });
      return { handler: route.handler, params };
    }
  }
  return { handler: renderNotFound, params: {} };
}

// ---------- Router ----------
function getCurrentPath() {
  const hash = window.location.hash.slice(1) || '/';
  const qIdx = hash.indexOf('?');
  return qIdx === -1 ? hash : hash.slice(0, qIdx);
}

function getQueryParams() {
  const hash = window.location.hash.slice(1);
  const qIdx = hash.indexOf('?');
  if (qIdx === -1) return {};
  return Object.fromEntries(new URLSearchParams(hash.slice(qIdx + 1)).entries());
}

async function router() {
  const path = getCurrentPath();
  const newHashUrl = `#${path}`;

  if (!popIfBack(newHashUrl)) {
    if (currentActivePath !== null && currentActivePath !== path) {
      const labelKey = PAGE_LABEL_KEYS[currentActivePath];
      if (labelKey) {
        pushRoute({ url: currentActiveFullUrl ?? `#${currentActivePath}`, labelKey });
      }
    }
  }
  currentActivePath = path;
  currentActiveFullUrl = window.location.hash || '#/';

  const backRoute = peekBack();
  const query = getQueryParams();
  const { handler, params } = matchRoute(path);
  const root = document.getElementById('app');

  window.scrollTo({ top: 0, behavior: 'instant' });

  try {
    await handler(root, { supabase, path, params, query, backRoute });

    if (path !== '/') {
      const effectiveBack = backRoute ?? getFallbackRoute(path);
      const mainEl = root.querySelector('main');
      if (mainEl) {
        const mainIsContainer = mainEl.classList.contains('container') || mainEl.classList.contains('container-narrow');
        const navClass = mainIsContainer ? 'page-back-nav' : 'page-back-nav container-wide';
        mainEl.prepend(el('nav', { class: navClass }, createBackButton(effectiveBack)));
      }
    }

    root.dataset.loading = 'false';
  } catch (err) {
    console.error('[router] Erreur de rendu :', err);
    root.innerHTML = `
      <div class="container" style="padding: 4rem 0; text-align: center;">
        <h1>${t('error.title')}</h1>
        <p class="muted">${t('error.body')}</p>
        <p class="muted" style="font-size: 0.875rem; margin-top: 1rem;">${err.message}</p>
      </div>
    `;
  }
}

window.addEventListener('hashchange', router);
window.addEventListener('DOMContentLoaded', router);
window.addEventListener('lang-change', router);

if (!import.meta.env.VITE_SUPABASE_URL) {
  console.warn(
    '[Census] VITE_SUPABASE_URL manquante. Crée un fichier .env.local à la racine du projet.'
  );
}
